/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import type ArticleContent from 'embercom/models/articles/article-content';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type RouterService from '@ember/routing/router-service';
import { objectNames } from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';

interface Args {
  activeContent: ArticleContent;
  editActiveContent: () => void;
  unpublishActiveContent: () => void;
  showSidePanel: boolean;
  toggleSidePanel: () => void;
  publishActiveContent: () => Promise<void>;
  scheduleActiveContentUpdate: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  onClose?: () => void;
  showDeleteButton?: boolean;
  showEditButton?: boolean;
  showPublishButton?: boolean;
  showUnpublishButton?: boolean;
  showLanguageSelector?: boolean;
  showReadOnlyButton?: boolean;
  showViewInKnowledgeButton?: boolean;
}

export default class Header extends Component<Args> {
  closeSidePanelOnResize = true;

  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare permissionsService: any;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked showDeleteConfirmModal = false;

  get showDeleteButton() {
    return this.args.showDeleteButton ?? true;
  }

  get showEditButton() {
    return this.args.showEditButton ?? true;
  }

  get showPublishButton() {
    return this.args.showPublishButton ?? true;
  }

  get showUnpublishButton() {
    return this.args.showUnpublishButton ?? true;
  }

  get showLanguageSelector() {
    if (this.appService.app.isStandaloneApp) {
      return false;
    }
    return this.args.showLanguageSelector ?? true;
  }

  get showReadOnlyButton() {
    return this.args.showReadOnlyButton ?? true;
  }

  get showViewInKnowledgeButton() {
    return this.args.showViewInKnowledgeButton ?? false;
  }

  get isPublishing() {
    return this.knowledgeHubEditorService.isPublishing;
  }

  @action
  viewInKnowledge() {
    safeWindowOpen(
      this.router.urlFor(
        'apps.app.knowledge-hub.view',
        objectNames[this.args.activeContent.entityType],
        this.args.activeContent.entityId,
      ),
      '_blank',
    );
  }

  @action closeSidePanel(header: HTMLElement) {
    let headerHasScroll = header.offsetWidth < header.scrollWidth;
    let closeSidePanel = headerHasScroll && this.closeSidePanelOnResize && this.args.showSidePanel;
    this.closeSidePanelOnResize = !headerHasScroll && this.args.showSidePanel;
    if (closeSidePanel) {
      // schedule the side panel to close after it finishes opening, to avoid a race condition
      schedule('afterRender', () => {
        this.args.toggleSidePanel();
      });
    }
  }

  @action async showDeleteConfirmationModal() {
    let activeContent = this.knowledgeHubEditorService.activeContentModel;

    try {
      await this.permissionsService.checkPermission(
        activeContent.requiredDeletePermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }
    this.showDeleteConfirmModal = true;
  }

  @dropTask
  *deleteActiveContent() {
    this.showDeleteConfirmModal = false;
    yield this.knowledgeHubEditorService.deleteActiveContent();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::View::Header': typeof Header;
    'knowledge-hub/content-editor/article-content/view/header': typeof Header;
  }
}
